import React, { useCallback, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Link2,
  RefreshCw,
  Server,
  ShieldCheck,
  Wifi,
  WifiOff,
  XCircle
} from 'lucide-react';
import { simcuvService } from '../../../services/api';
import usePolling from '../../../hooks/usePolling';
import Modal from '../PuntoV/Modal';
import { fmtFechaHora } from '../PuntoV/formato';

const INTERVALO_MS = 30000;

const ESTADOS = {
  conectado: {
    etiqueta: 'Conectado',
    detalle: 'SIMCUV respondió con el contrato 1.',
    clase: 'border-green-200 bg-green-50 text-green-800',
    icono: CheckCircle2
  },
  no_configurado: {
    etiqueta: 'No configurado',
    detalle: 'Faltan la URL o alguna de las claves de la integración en el ERP.',
    clase: 'border-amber-200 bg-amber-50 text-amber-800',
    icono: AlertTriangle
  },
  timeout: {
    etiqueta: 'Sin respuesta (timeout)',
    detalle: 'SIMCUV no respondió dentro de los 5 segundos.',
    clase: 'border-red-200 bg-red-50 text-red-800',
    icono: Clock
  },
  clave_rechazada: {
    etiqueta: 'Clave rechazada',
    detalle: 'SIMCUV respondió, pero rechazó la clave saliente del ERP.',
    clase: 'border-red-200 bg-red-50 text-red-800',
    icono: ShieldCheck
  },
  no_disponible: {
    etiqueta: 'No disponible',
    detalle: 'SIMCUV no está disponible o devolvió una respuesta inválida.',
    clase: 'border-red-200 bg-red-50 text-red-800',
    icono: XCircle
  },
  incompatible: {
    etiqueta: 'Contrato incompatible',
    detalle: 'SIMCUV respondió con un contrato distinto de 1.',
    clase: 'border-amber-200 bg-amber-50 text-amber-800',
    icono: AlertTriangle
  },
  consultando: {
    etiqueta: 'Consultando...',
    detalle: 'Comprobando la conexión con SIMCUV.',
    clase: 'border-gray-200 bg-gray-50 text-gray-700',
    icono: RefreshCw
  },
  error_propio: {
    etiqueta: 'No se pudo consultar',
    detalle: 'El ERP no respondió a la consulta de estado. Se reintentará automáticamente.',
    clase: 'border-red-200 bg-red-50 text-red-800',
    icono: AlertTriangle
  }
};

const Dato = ({ etiqueta, valor, icono: Icono, ayuda }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {Icono && <Icono size={14} />}
      {etiqueta}
    </div>
    <div className="mt-1 text-lg font-semibold text-gray-800 break-words">{valor}</div>
    {ayuda && <p className="mt-0.5 text-xs text-gray-400">{ayuda}</p>}
  </div>
);

const DatoDetalle = ({ etiqueta, valor }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{etiqueta}</dt>
    <dd className="mt-0.5 text-sm text-gray-800 break-words">{valor}</dd>
  </div>
);

const SimcuvView = () => {
  const [estado, setEstado] = useState(null);
  const [errorPropio, setErrorPropio] = useState('');
  const [detalle, setDetalle] = useState(false);

  const cargar = useCallback(async () => {
    setErrorPropio('');
    try {
      const data = await simcuvService.estado();
      setEstado(data);
    } catch (err) {
      setErrorPropio(err?.message || 'No se pudo consultar el estado de la integración.');
      throw err;
    }
  }, []);

  const { actualizando: cargando, refrescar: consultar } = usePolling(cargar, INTERVALO_MS);

  const visual = useMemo(() => {
    if (!estado) return errorPropio ? ESTADOS.error_propio : ESTADOS.consultando;

    if (estado.error === null && estado.alcanzable && estado.contrato_compatible) {
      return ESTADOS.conectado;
    }
    if (estado.error === null) return ESTADOS.no_disponible;

    return ESTADOS[estado.error] || ESTADOS.no_disponible;
  }, [estado, errorPropio]);

  const IconoEstado = visual.icono;
  const compatible = estado?.contrato_compatible === true;
  const alcanzable = estado?.alcanzable === true;

  return (
    <div className="p-2 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Link2 className="text-blue-600" />
            SIMCUV
          </h2>
          <p className="text-sm lg:text-base text-gray-600">
            Estado de la conexión servidor a servidor con SIMCUV.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <button
            type="button"
            onClick={consultar}
            disabled={cargando}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium disabled:opacity-60"
          >
            <RefreshCw size={16} className={cargando ? 'animate-spin' : ''} />
            {cargando ? 'Comprobando...' : 'Probar conexión'}
          </button>
          <button
            type="button"
            onClick={() => setDetalle(true)}
            disabled={!estado}
            className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            Ver detalle
          </button>
        </div>
      </div>

      {errorPropio && estado && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>No se pudo actualizar el estado; se muestra la última comprobación conocida.</span>
        </div>
      )}

      <div className={`rounded-xl border p-4 ${visual.clase}`}>
        <div className="flex items-start gap-3">
          <IconoEstado size={22} className={`mt-0.5 shrink-0 ${visual === ESTADOS.consultando ? 'animate-spin' : ''}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold">{visual.etiqueta}</p>
            <p className="text-sm opacity-80">{visual.detalle}</p>
            {errorPropio && !estado && (
              <p className="mt-1 text-xs opacity-80">{errorPropio}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Dato
          etiqueta="Configurado"
          icono={Database}
          valor={estado ? (estado.configurado ? 'Sí' : 'No') : '—'}
          ayuda="URL y ambas claves presentes en el ERP."
        />
        <Dato
          etiqueta="Alcanzable"
          icono={alcanzable ? Wifi : WifiOff}
          valor={estado ? (alcanzable ? 'Sí' : 'No') : '—'}
          ayuda="Hubo respuesta HTTP de SIMCUV."
        />
        <Dato
          etiqueta="Latencia"
          icono={Activity}
          valor={estado?.latencia_ms != null ? `${estado.latencia_ms} ms` : '—'}
          ayuda="Tiempo de la última comprobación."
        />
        <Dato
          etiqueta="Versión de SIMCUV"
          icono={Server}
          valor={estado?.version || '—'}
          ayuda="Versión que informó el otro lado."
        />
        <Dato
          etiqueta="Contrato"
          icono={Link2}
          valor={estado?.contrato || '—'}
          ayuda={estado ? (estado.contrato ? (compatible ? 'Compatible con el contrato 1.' : 'No compatible con el contrato 1.') : 'Sin contrato informado.') : 'Se comprobará al consultar.'}
        />
        <Dato
          etiqueta="Última verificación"
          icono={Clock}
          valor={estado?.verificado_en ? fmtFechaHora(estado.verificado_en) : '—'}
          ayuda="Se actualiza cada 30 segundos."
        />
      </div>

      <p className="text-xs text-gray-400">
        La comprobación automática corre cada 30 segundos. El botón manual fuerza una comprobación inmediata.
      </p>

      {detalle && (
        <Modal
          titulo="Detalle de la integración"
          subtitulo="SIMCUV ↔ ERP"
          icono={Link2}
          onClose={() => setDetalle(false)}
          ancho="lg"
        >
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DatoDetalle etiqueta="Estado" valor={visual.etiqueta} />
            <DatoDetalle etiqueta="Configurado" valor={estado?.configurado ? 'Sí' : 'No'} />
            <DatoDetalle etiqueta="Alcanzable" valor={alcanzable ? 'Sí' : 'No'} />
            <DatoDetalle etiqueta="Latencia" valor={estado?.latencia_ms != null ? `${estado.latencia_ms} ms` : '—'} />
            <DatoDetalle etiqueta="Versión" valor={estado?.version || '—'} />
            <DatoDetalle etiqueta="Contrato" valor={estado?.contrato || '—'} />
            <DatoDetalle etiqueta="Contrato compatible" valor={compatible ? 'Sí' : 'No'} />
            <DatoDetalle etiqueta="Verificado en" valor={estado?.verificado_en ? fmtFechaHora(estado.verificado_en) : '—'} />
          </dl>
          <p className="mt-4 text-xs text-gray-500">
            El ERP solo muestra categorías de error; nunca el texto crudo que devuelve SIMCUV.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default SimcuvView;
